import { SpaceType } from "@components/atoms/Spacer/types";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { useTheme } from "styled-components/native";
import { Platform, Pressable } from "react-native";
import { SelectionType } from "@components/atoms/Filters/types";
import ImageTextFilterItem from "@components/atoms/Filters/ImageTextListSection/ImageTextFilterItem";
import ShowMoreButton from "@components/atoms/Filters/ShowMoreButton";
import { VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useDispatch } from "react-redux";
import {
  removeFiltersPayload,
  setFiltersPayload,
  useSearchFiltersAuxPayload,
  useSearchFiltersSlice,
  useValueFromPayloadRangeOrSingleValue,
} from "@redux/slices/searchFilters";
import SelectedItemsTitle from "@components/atoms/Filters/SelectedItemsTitle";
import { SelectedItemTheme } from "@components/atoms/Button/types";
import useResetFilter from "../../../../hooks/useResetFilter";
import useRemoveColorFilters from "../FilterHooks/UseRemoveColorFilters";
import { FilterScreenContext } from "@scenes/explore/FilterScreenContext";
import { ProductCategory, screenWidth } from "@utils/constants";

type ImageTextListRangeSectionProps = {
  options: Array<any>;
  columns: number;
  selectionType?: string;
  displayedRows?: number;
  sectionTitle?: string;
  payloadName?: Array<string>;
  value?: any[];
  sectionName?: string;
  parentFilter?: string;
  selectedStyle: SelectedItemTheme;
  minWidth?: number;
  selectedProduct: ProductCategory;
  parentKey?: string;
};

const ImageTextListRangeSection = ({
  options,
  selectionType,
  displayedRows,
  sectionTitle,
  payloadName,
  sectionName,
  parentFilter,
  selectedStyle,
  minWidth = 72,
  selectedProduct,
  columns,
  parentKey,
}: ImageTextListRangeSectionProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const dispatch = useDispatch();
  const isTouched = useRef(false);
  const value = useValueFromPayloadRangeOrSingleValue(
    sectionName,
    payloadName,
    selectionType === SelectionType.range,
    parentKey
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { auxPayload: filtersPayload } = useSearchFiltersSlice();
  let auxPayload = useSearchFiltersAuxPayload();
  const previouslySelected = useMemo(() => {
    let selected = options?.filter((option) =>
      value?.includes(option.search_payload?.[0])
    );

    if (selectionType === SelectionType.range) {
      const rangeMin = options?.findIndex((obj) => obj?.name === value?.[0]);
      const rangeMax = options?.findIndex((obj) => obj?.name === value?.[1]);
      selected = options.slice(rangeMin, rangeMax + 1);
    }
    return selected || [];
  }, [options, value, selectionType]);

  const { containerWidth, fromResultScreen } = useContext(FilterScreenContext);
  const [selectedItems, setSelectedItems] = useState<any[]>(
    previouslySelected || []
  );
  useResetFilter(() => {
    setStartRangeIndex(null);
    setSelectedItems([]);
  });
  const [showAll, setShowAll] = useState<boolean>(!displayedRows);
  const [startRangeIndex, setStartRangeIndex] = useState<number | null>(null);
  const [parentValue, setParentValue] = useState(null);

  useRemoveColorFilters(sectionName);

  useEffect(() => {
    const parentFilterValue = parentFilter
      ? filtersPayload?.[sectionName]?.[parentFilter]
      : null;

    const selectedNestedValue = options
      ?.find((o) => o?.name === parentFilterValue)
      ?.options?.filter((o: any) => value?.includes(o.search_payload?.[0]));

    if (selectedNestedValue?.length > 0) setSelectedItems(selectedNestedValue);

    if (parentFilterValue !== parentValue) {
      setParentValue(parentFilterValue);
    }
  }, [parentFilter, filtersPayload?.[sectionName]?.[parentFilter]]);

  const getOptions = () => {
    if (parentValue) {
      const parentOption = options.find(
        (option) => option.name === parentValue
      );
      if (parentOption?.options?.length > 0) {
        return parentOption.options;
      }
      return [];
    }
    return options;
  };

  const itemsPerRow =
    Platform.OS === "web" || Platform.OS === "android"
      ? Math.round(containerWidth / (minWidth + SpaceType.Basic))
      : Math.floor(containerWidth / (minWidth + SpaceType.Basic));

  const columnsWidth = useMemo(() => {
    if (theme.isMobile) {
      return (
        (screenWidth -
          (2 * SpaceType.Double +
            2 * SpaceType.Double +
            columns * SpaceType.Basic) +
          columns -
          2) /
        columns
      );
    }
    const columnWidth = Math.floor(
      (containerWidth -
        (itemsPerRow - 1) * SpaceType.Basic -
        (fromResultScreen ? 0 : 2 * SpaceType.Double)) /
        itemsPerRow
    );
    return columnWidth;
  }, [itemsPerRow, containerWidth]);

  const rows = Math.min(
    displayedRows ?? 0,
    Math.ceil(getOptions().length / itemsPerRow)
  );

  useEffect(() => {
    if (isTouched.current && parentFilter) {
      if (sectionName && payloadName) {
        dispatch(removeFiltersPayload({ sectionName, payloadName }));
        setSelectedItems([]);
      }
    } else {
      if (parentValue) isTouched.current = true;
    }
  }, [parentValue]);

  const filteredOptions = useMemo(() => {
    if (!itemsPerRow) return [];
    const initialOptions = getOptions();

    const numberOfItems = initialOptions.length;
    let numberOfPlaceholders = itemsPerRow - (numberOfItems % itemsPerRow);
    if (numberOfItems % itemsPerRow === 0) {
      numberOfPlaceholders = 0;
    }
    return [...initialOptions, ...Array(numberOfPlaceholders).fill({})];
  }, [parentValue, options, itemsPerRow]);

  const handleItemSelection = useCallback(
    (item: any, index: number) => {
      isTouched.current = true;

      if (selectionType === SelectionType.single) {
        if (selectedItems.includes(item)) {
          setSelectedItems([]);
        } else {
          setSelectedItems([item]);
        }
      } else if (selectionType === SelectionType.multiple) {
        setSelectedItems((prevSelected) =>
          prevSelected.includes(item)
            ? prevSelected.filter((prevItem) => prevItem !== item)
            : [...prevSelected, item]
        );
      } else if (selectionType === SelectionType.range) {
        if (startRangeIndex === null) {
          setStartRangeIndex(index);
          setSelectedItems([item]);
        } else {
          const minIndex = Math.min(startRangeIndex, index);
          const maxIndex = Math.max(startRangeIndex, index);
          const range = options.slice(minIndex, maxIndex + 1);
          const currentMin = options.indexOf(selectedItems[0]);
          const currentMax = options.indexOf(
            selectedItems[selectedItems.length - 1]
          );
          if (selectedItems.length === 1 && currentMax !== index) {
            setSelectedItems(range);
          } else {
            if (index === currentMin - 1) {
              setSelectedItems([item, ...selectedItems]);
            } else if (index === currentMax + 1) {
              setSelectedItems([...selectedItems, item]);
            } else if (index === currentMin) {
              setStartRangeIndex(index + 1);
              setSelectedItems(selectedItems.slice(1));
            } else if (index === currentMax) {
              setStartRangeIndex(index - 1);
              setSelectedItems(selectedItems.slice(0, -1));
            } else {
              setStartRangeIndex(index);
              setSelectedItems([item]);
            }
          }
        }
      }
    },
    [selectionType, startRangeIndex, options, selectedItems]
  );

  useEffect(() => {
    if (!isTouched.current) return;

    if (selectedItems.length === 0 && sectionName && payloadName) {
      dispatch(
        removeFiltersPayload({
          sectionName,
          payloadName,
          key: parentKey === "argyle" ? parentKey : "",
        })
      );
    } else {
      const payload: any = { ...auxPayload[sectionName] };
      if (selectionType === SelectionType.range) {
        const key = Object.keys(payloadName as any)[0];
        const values = payloadName?.[key as any];
        if (parentKey) {
          const parentObject = payload[parentKey]
            ? { ...payload[parentKey] }
            : {};
          parentObject[key] = {
            [(values as any)[0]]: selectedItems[0]?.search_payload[0],
            [(values as any)[1]]:
              selectedItems[selectedItems.length - 1]?.search_payload[0],
          };
          payload[parentKey] = parentObject;
        } else {
          payload[key] = {
            [(values as any)[0]]: selectedItems[0]?.search_payload[0],
            [(values as any)[1]]:
              selectedItems[selectedItems.length - 1]?.search_payload[0],
          };
        }
      } else if (selectionType === SelectionType.single) {
        payload[(payloadName as any)[0]] = selectedItems[0]?.search_payload[0];
      } else {
        payloadName?.forEach((name) => {
          payload[name] = selectedItems
            ?.map((item) => item?.search_payload)
            .flat();
        });
      }
      if (sectionName) {
        dispatch(setFiltersPayload({ sectionName, data: payload }));
      }
    }
  }, [selectedItems]);

  return parentFilter && !parentValue ? null : (
    <ParentView>
      {sectionTitle &&
        filteredOptions?.length > 0 &&
        filteredOptions?.filter((obj) => Object.keys(obj)?.length > 0).length >
          0 && (
          <TitleSection>
            <VDBText isBold type={typography["H3-M"]} color={theme["core-05"]}>
              {sectionTitle}
            </VDBText>
            {selectedItems.length > 0 && (
              <SelectedItemsTitle
                selectionType={selectionType}
                selectedItems={selectedItems}
              />
            )}
          </TitleSection>
        )}
      <FilterView>
        {filteredOptions
          ?.slice(0, showAll ? filteredOptions?.length : rows * itemsPerRow)
          .map((option, index) => (
            <Pressable
              onHoverIn={() => setHoveredIndex(index)}
              onHoverOut={() => setHoveredIndex(null)}
              key={index}
              onPress={() => {
                if (option.name) handleItemSelection(option, index);
              }}
              disabled={!option.name}
            >
              <ImageTextFilterItem
                key={index}
                item={option}
                screenWidth={containerWidth}
                isSelected={selectedItems.includes(option)}
                selectedStyle={selectedStyle}
                width={columnsWidth}
                isHovered={index === hoveredIndex}
              />
            </Pressable>
          ))}
      </FilterView>
      <ShowMoreButton
        options={filteredOptions}
        columns={itemsPerRow}
        showAll={showAll}
        onPressFunction={setShowAll}
        displayedRows={rows}
        selectedProduct={selectedProduct}
      />
    </ParentView>
  );
};

const TitleSection = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
`;

const FilterView = styled.View`
  gap: ${SpaceType.Basic}px;
  justify-content: space-between;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ParentView = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

export default memo(ImageTextListRangeSection);
